import {
  CatalogoDeInformesSchema,
  EjecucionDeInformeSchema,
  EstadoDeAjustesSchema,
  EstadoDeCartaDeInstruccionSchema,
  EstadoDeDocumentosSchema,
  ExpedienteSchema,
  ListaDePlantillasSchema,
  PlantillaSchema,
  PlantillaSubidaSchema,
  PreciosDeRegaliasSchema,
  ResultadoDeArchivoDeCarpetaDeExportacionesSchema,
  ResultadoDeGeneracionSchema,
  ResultadoDeRevisionDeCarpetaDeExportacionesSchema,
  ResultadoDelExpedienteDesdeDropboxSchema,
  type AjustesDeDocumentos,
  type CategoriaDeDocumento,
  type ClaveDeInforme,
  type EntradasBluequest,
  type EntradasDeAduana,
  type EntradasDeCartaDeInstruccion,
  type EntradasDeFacturacion,
  type PaqueteDeDocumentos,
  type TipoDePlantilla,
} from '@cnc/contracts';
import {useMutation, useQuery, useQueryClient} from '@tanstack/react-query';
import {trx, txt, type Texto} from '../i18n/idioma';
import {RAIZ_DE_EXPORTACIONES} from './carpetas';
import {json, pedir} from './cliente';

// ── Documentos de una exportacion ───────────────────────────────────────────

const rutaDeExportacion = (codigo: string) => `seguimiento/exportaciones/${encodeURIComponent(codigo)}`;

export function useEstadoDeDocumentos(codigo: string) {
  return useQuery({
    queryKey: ['documentos', 'estado', codigo],
    queryFn: () => pedir(`${rutaDeExportacion(codigo)}/documentos`, EstadoDeDocumentosSchema),
    retry: false,
  });
}

type Generacion =
  | {paquete: 'departure' | 'control-de-empaque' | 'certificados-de-origen'}
  | {paquete: 'instrucciones-de-facturacion'; entradas: EntradasDeFacturacion}
  | {paquete: 'documentos-de-aduana'; entradas: EntradasDeAduana}
  | {paquete: 'bluequest'; entradas: EntradasBluequest};

export function useGenerarDocumentos(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (g: Generacion) =>
      pedir(
        `${rutaDeExportacion(codigo)}/documentos/${g.paquete}`,
        ResultadoDeGeneracionSchema,
        json('POST', 'entradas' in g ? g.entradas : {}),
      ),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['documentos', 'estado', codigo]});
      // Los generados quedan tambien en Dropbox (decision 0092).
      await cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]});
      // Los documentos de aduana guardan las facturas por mina, que el detalle muestra.
      await cliente.invalidateQueries({queryKey: ['seguimiento', 'exportacion', codigo]});
      await cliente.invalidateQueries({queryKey: ['financiacion', 'exportacion', codigo]});
    },
  });
}

export const rutaDeDocumentoGenerado = (id: number) => `/api/documentos/generados/${id}/archivo`;
export const rutaDelZipDelLote = (codigo: string, lote: string) =>
  `/api/${rutaDeExportacion(codigo)}/documentos/lotes/${encodeURIComponent(lote)}/zip`;
const ETIQUETAS_DE_PAQUETE_CORTAS: Record<PaqueteDeDocumentos, Texto> = {
  'instrucciones-de-facturacion': txt('Instrucciones de facturación', 'Invoicing instructions'),
  'control-de-empaque': txt('Control de empaque', 'Packing control'),
  departure: txt('Departure + Packing List', 'Departure + Packing List'),
  'documentos-de-aduana': txt('Documentos de aduana', 'Customs documents'),
  'certificados-de-origen': txt('Tabla relación certificados', 'Certificates relation table'),
  bluequest: txt('Documentos Bluequest', 'Bluequest documents'),
  'carta-de-instruccion': txt('Carta de instrucción (SLI)', 'Letter of instruction (SLI)'),
};

// Carta de instruccion del remitente a Brink's (SLI, 24/10/2026): para
// cualquier exportacion (propias, Mineros y terceros).
export function useCartaDeInstruccion(codigo: string) {
  return useQuery({
    queryKey: ['documentos', 'carta-de-instruccion', codigo],
    queryFn: () =>
      pedir(`${rutaDeExportacion(codigo)}/documentos/carta-de-instruccion`, EstadoDeCartaDeInstruccionSchema),
    retry: false,
  });
}

export function useGenerarCartaDeInstruccion(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (entradas: EntradasDeCartaDeInstruccion) =>
      pedir(
        `${rutaDeExportacion(codigo)}/documentos/carta-de-instruccion`,
        ResultadoDeGeneracionSchema,
        json('POST', entradas),
      ),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['documentos', 'carta-de-instruccion', codigo]});
      await cliente.invalidateQueries({queryKey: ['documentos', 'estado', codigo]});
    },
  });
}
// Nombre corto del paquete en el idioma activo.
export const etiquetaDePaqueteCorta = (paquete: PaqueteDeDocumentos): string =>
  trx(ETIQUETAS_DE_PAQUETE_CORTAS[paquete]);

export function usePreciosDeRegalias(activo = true) {
  return useQuery({
    queryKey: ['documentos', 'precios-regalias'],
    queryFn: () => pedir('documentos/precios-regalias', PreciosDeRegaliasSchema),
    enabled: activo,
    staleTime: 5 * 60_000,
  });
}

// ── Expediente ──────────────────────────────────────────────────────────────

export function useSubirAlExpediente(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({archivo, categoria}: {archivo: File; categoria: CategoriaDeDocumento}) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo, archivo.name);
      cuerpo.append('categoria', categoria);
      return pedir(`${rutaDeExportacion(codigo)}/expediente`, ExpedienteSchema, {method: 'POST', body: cuerpo});
    },
    onSuccess: async () => {
      // Tambien queda en la carpeta de Dropbox (decision 0085).
      await cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]});
      await cliente.invalidateQueries({queryKey: ['seguimiento', 'exportacion', codigo]});
      await cliente.invalidateQueries({queryKey: ['financiacion', 'exportacion', codigo]});
    },
  });
}

export function useBorrarDelExpediente(codigo: string) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (id: number) => pedir(`expediente/${id}`, ExpedienteSchema, json('DELETE')),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]});
      await cliente.invalidateQueries({queryKey: ['seguimiento', 'exportacion', codigo]});
      await cliente.invalidateQueries({queryKey: ['financiacion', 'exportacion', codigo]});
    },
  });
}

// Trae al expediente lo que hay en la carpeta de Dropbox y aun no esta (decision 0092).
export function useTraerDeDropbox(codigo: string, raiz = RAIZ_DE_EXPORTACIONES) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: () =>
      pedir(
        `${raiz}/${encodeURIComponent(codigo)}/expediente/desde-dropbox`,
        ResultadoDelExpedienteDesdeDropboxSchema,
        json('POST'),
      ),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['seguimiento', 'exportacion', codigo]});
      await cliente.invalidateQueries({queryKey: ['carpetas', 'archivos', codigo]});
      await cliente.invalidateQueries({queryKey: ['financiacion', 'exportacion', codigo]});
      await cliente.invalidateQueries({queryKey: ['dore', 'expediente', codigo]});
    },
  });
}

export const rutaDeDocumentoDelExpediente = (id: number) => `/api/expediente/${id}/archivo`;
export const rutaDelZipDelExpediente = (codigo: string, raiz = RAIZ_DE_EXPORTACIONES) =>
  `/api/${raiz}/${encodeURIComponent(codigo)}/expediente/zip`;

// ── Carpeta de exportaciones de Dropbox (decision 0070) ─────────────────────

// Ruta completa desde la carpeta elegida: si se elige una sola exportacion,
// su nombre es lo que dice a que expediente va.
export const rutaEnLaCarpetaDeExportaciones = (archivo: File) => archivo.webkitRelativePath || archivo.name;

export function revisarCarpetaDeExportaciones(archivos: File[]) {
  const lista = {
    archivos: archivos.map(a => ({
      ruta: rutaEnLaCarpetaDeExportaciones(a),
      tamanoBytes: a.size,
      modificado: new Date(a.lastModified).toISOString(),
    })),
  };
  const cuerpo = new FormData();
  cuerpo.append('lista', new Blob([JSON.stringify(lista)], {type: 'application/json'}), 'lista.json');
  return pedir('expediente/carpeta/revisar', ResultadoDeRevisionDeCarpetaDeExportacionesSchema, {
    method: 'POST',
    body: cuerpo,
  });
}

export function subirArchivoDeCarpetaDeExportaciones(archivo: File) {
  const cuerpo = new FormData();
  cuerpo.append('ruta', rutaEnLaCarpetaDeExportaciones(archivo));
  cuerpo.append('archivo', archivo, archivo.name);
  return pedir('expediente/carpeta/archivo', ResultadoDeArchivoDeCarpetaDeExportacionesSchema, {
    method: 'POST',
    body: cuerpo,
  });
}

// ── Informes ────────────────────────────────────────────────────────────────

export function useInformes() {
  return useQuery({
    queryKey: ['informes'],
    queryFn: () => pedir('informes', CatalogoDeInformesSchema),
  });
}

export function useEjecutarInforme() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({clave, incluirHistorial}: {clave: ClaveDeInforme; incluirHistorial: boolean}) =>
      pedir(`informes/${clave}${incluirHistorial ? '?historial=true' : ''}`, EjecucionDeInformeSchema, json('POST')),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['informes']});
    },
  });
}

export const rutaDeInformeGenerado = (id: number) => `/api/informes/ejecuciones/${id}/archivo`;

// Lo filtrado en pantalla en el formato del archivo base (Consolidated o
// Status), para compartir; queda tambien en el historial de informes.
export function useDescargaFiltrada(informe: ClaveDeInforme) {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (cadena: string) =>
      pedir(`informes/${informe}/filtrado${cadena ? `?${cadena}` : ''}`, EjecucionDeInformeSchema, json('POST')),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['informes']});
    },
  });
}

// ── Administracion: plantillas y datos de documentos ────────────────────────

// Sin empresa: las generales. Con empresa: logo, firma y ensayo propios (decision 0061).
const conEmpresa = (empresaId?: number | null) => (empresaId ? `?empresa=${empresaId}` : '');

export function usePlantillas(empresaId?: number | null) {
  return useQuery({
    queryKey: ['documentos', 'plantillas', empresaId ?? null],
    queryFn: () => pedir(`documentos/plantillas${conEmpresa(empresaId)}`, ListaDePlantillasSchema),
  });
}

export function useSubirPlantilla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({
      tipo,
      archivo,
      notas,
      empresaId,
    }: {
      tipo: TipoDePlantilla;
      archivo: File;
      notas?: string;
      empresaId?: number | null;
    }) => {
      const cuerpo = new FormData();
      cuerpo.append('archivo', archivo, archivo.name);
      if (notas?.trim()) cuerpo.append('notas', notas.trim());
      return pedir(`documentos/plantillas/${tipo}${conEmpresa(empresaId)}`, PlantillaSubidaSchema, {
        method: 'POST',
        body: cuerpo,
      });
    },
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['documentos']});
      await cliente.invalidateQueries({queryKey: ['informes']});
    },
  });
}

export function useActivarPlantilla() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: ({tipo, id, empresaId}: {tipo: TipoDePlantilla; id: number; empresaId?: number | null}) =>
      pedir(
        `documentos/plantillas/${tipo}/versiones/${id}/activar${conEmpresa(empresaId)}`,
        PlantillaSchema,
        json('POST'),
      ),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['documentos']});
    },
  });
}

export const rutaDePlantilla = (tipo: TipoDePlantilla, version?: number, empresaId?: number | null) => {
  const consulta = new URLSearchParams();
  if (version) consulta.set('version', String(version));
  if (empresaId) consulta.set('empresa', String(empresaId));
  const cadena = consulta.toString();
  return `/api/documentos/plantillas/${tipo}/archivo${cadena ? `?${cadena}` : ''}`;
};

export function useAjustesDeDocumentos() {
  return useQuery({
    queryKey: ['documentos', 'ajustes'],
    queryFn: () => pedir('documentos/ajustes', EstadoDeAjustesSchema),
  });
}

export function useGuardarAjustesDeDocumentos() {
  const cliente = useQueryClient();
  return useMutation({
    mutationFn: (ajustes: AjustesDeDocumentos) =>
      pedir('documentos/ajustes', EstadoDeAjustesSchema, json('PUT', ajustes)),
    onSuccess: async () => {
      await cliente.invalidateQueries({queryKey: ['documentos']});
    },
  });
}
